import { Controller } from "@hotwired/stimulus"
import html2canvas from "html2canvas"

export default class extends Controller {
  static targets = ["button"]

  declare readonly buttonTarget: HTMLElement

  // Download the grid as an image
  async download() {
    try {
      this.buttonTarget.textContent = "⏳ Generating..."
      this.buttonTarget.setAttribute("disabled", "true")

      const canvas = await this.captureGrid()
      const dataUrl = canvas.toDataURL("image/png")

      // Create download link
      const link = document.createElement("a")
      link.download = `mood-journal-${this.getCurrentDate()}.png`
      link.href = dataUrl
      link.click()

      this.resetButton()
    } catch (error) {
      console.error("Error generating image:", error)
      this.buttonTarget.textContent = "❌ Error"
      setTimeout(() => this.resetButton(), 2000)
    }
  }

  // Share on social media using Web Share API
  async share() {
    try {
      this.buttonTarget.textContent = "⏳ Generating..."
      this.buttonTarget.setAttribute("disabled", "true")

      const canvas = await this.captureGrid()

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob)
          else reject(new Error("canvas.toBlob returned null"))
        }, "image/png")
      })

      const file = new File([blob], `mood-journal-${this.getCurrentDate()}.png`, {
        type: "image/png",
      })

      // Check if Web Share API is supported
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: "My Mood Journal",
          text: "Check out my Year in Pixels mood tracker!",
          files: [file],
        })
      } else {
        // Fallback to download if sharing is not supported
        await this.download()
      }

      this.resetButton()
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        console.error("Error sharing:", error)
        this.buttonTarget.textContent = "❌ Error"
        setTimeout(() => this.resetButton(), 2000)
      } else {
        this.resetButton()
      }
    }
  }

  // Capture the grid as a canvas
  private async captureGrid(): Promise<HTMLCanvasElement> {
    // Find the grid wrapper element
    const gridWrapper = document.querySelector(".timeline-section") as HTMLElement
    if (!gridWrapper) {
      throw new Error("Timeline section not found")
    }

    // Detect current theme
    const isDark = document.documentElement.style.colorScheme === "dark" || 
                   (!document.documentElement.style.colorScheme && window.matchMedia("(prefers-color-scheme: dark)").matches)
    const bgColor = isDark ? "#0f172a" : "#ffffff" // Updated to match user CSS variables --bg-page
    const watermarkColor = isDark ? "#9CA3AF" : "#9CA3AF"

    // Clone the grid to avoid modifying the original
    const clone = gridWrapper.cloneNode(true) as HTMLElement
    clone.style.position = "absolute"
    clone.style.left = "-9999px"
    clone.style.background = bgColor
    clone.style.padding = "40px"
    clone.style.paddingBottom = "60px" // Extra space for watermark
    clone.style.borderRadius = "12px"

    // Add watermark
    const watermark = document.createElement("div")
    watermark.textContent = "howifeel.today"
    watermark.style.cssText = `
      position: absolute;
      bottom: 20px;
      right: 40px;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 12px;
      font-weight: 500;
      color: ${watermarkColor};
      opacity: 0.6;
      letter-spacing: 0.5px;
    `
    clone.appendChild(watermark)

    document.body.appendChild(clone)

    try {
      const canvas = await html2canvas(clone, {
        backgroundColor: bgColor,
        scale: 2, // Higher quality
        logging: false,
        useCORS: true,
        allowTaint: true,
      })
      return canvas
    } finally {
      document.body.removeChild(clone)
    }
  }

  private getCurrentDate(): string {
    const now = new Date()
    return now.toISOString().split("T")[0]
  }

  private resetButton() {
    this.buttonTarget.textContent = "📸 Share"
    this.buttonTarget.removeAttribute("disabled")
  }
}
